import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Title,
  Text,
  Paper,
  Group,
  Button,
  Stack,
  TextInput,
  Textarea,
  Select,
  ColorInput,
  Alert,
  SimpleGrid,
  Box,
  ThemeIcon,
  Avatar,
  Card,
  Badge,
  Divider,
  NumberInput,
  Switch,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { FileUploader } from "../../../../components/FileUploader";
import {
  IconArrowLeft,
  IconDeviceFloppy,
  IconPalette,
  IconDetails,
  IconPhoneCheck,
  IconLock,
  IconGift,
} from "@tabler/icons-react";
import { Endpoints } from "../../../../lib/endpoints";
import {
  PerformRequest,
  UploadFiles,
} from "../../../../hooks/usePerformRequest";
import { HttpCodes } from "../../../../lib/data";

const CampaignCreate = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);

  const [bannerFile, setBannerFile] = useState<File | null>(null);
  const [levels, setLevels] = useState<any[]>([]);

  useEffect(() => {
    document.title = "New Campaign | Virel Admin";
    const fetchLevels = async () => {
      const endpoint = Endpoints.GetLevels();
      const r = await PerformRequest<any>({
        method: endpoint.method,
        route: endpoint.route,
      });
      if (r.status === HttpCodes.Success) {
        setLevels(r.data.data || []);
      }
    };
    fetchLevels();
  }, []);

  const form = useForm({
    initialValues: {
      title: "",
      clientName: "",
      description: "",
      status: "draft",
      primaryColor: "#19752e",
      secondaryColor: "#E8FBF6",
      logoUrl: "",
      bannerUrl: "",
      endDate: null as Date | null,
      minLevel: 1,
      eligibility: {
        type: "open",
        minMissionCompletions: 0,
      },
      completionBonus: {
        amount: 0,
        currency: "COIN",
      },
      isLocked: false,
      lockReason: "",
      squadsOnly: false,
    },
    validate: {
      title: (v) => (!v.trim() ? "Title is required" : null),
      clientName: (v) => (!v.trim() ? "Client name is required" : null),
      endDate: (v) =>
        v && v < new Date(Date.now() + 86400000)
          ? "Expiry date must be at least 1 day in the future"
          : null,
    },
  });

  const submit = async () => {
    setIsSubmitting(true);
    setError(null);

    const validation = form.validate();
    if (validation.hasErrors) {
      setIsSubmitting(false);
      notifications.show({
        title: "Validation Error",
        message: "Please fix the form errors before proceeding.",
        color: "red",
      });
      // Scroll to first error field
      const firstErrorKey = Object.keys(validation.errors)[0];
      if (firstErrorKey) {
        const errorElement = document.querySelector(
          `[name="${firstErrorKey}"], #${firstErrorKey}, [id*="${firstErrorKey}"]`,
        );
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
          (errorElement as HTMLElement).focus?.();
        }
      }
      return;
    }

    let finalLogoUrl = form.values.logoUrl;
    let finalBannerUrl = form.values.bannerUrl;

    try {
      const logoPromise = logoFile
        ? UploadFiles([logoFile])
        : Promise.resolve(null);
      const bannerPromise = bannerFile
        ? UploadFiles([bannerFile])
        : Promise.resolve(null);

      const [logoRes, bannerRes] = await Promise.all([
        logoPromise,
        bannerPromise,
      ]);

      if (logoRes) {
        if (
          (logoRes[0].status === HttpCodes.Success ||
            logoRes[0].status === HttpCodes.Created) &&
          logoRes[0].data?.data?.url
        ) {
          finalLogoUrl = logoRes[0].data.data.url;
        } else {
          console.error("Logo upload failed response:", logoRes[0]);
          throw new Error("Logo upload failed");
        }
      }

      if (bannerRes) {
        if (
          (bannerRes[0].status === HttpCodes.Success ||
            bannerRes[0].status === HttpCodes.Created) &&
          bannerRes[0].data?.data?.url
        ) {
          finalBannerUrl = bannerRes[0].data.data.url;
        } else {
          console.error("Banner upload failed response:", bannerRes[0]);
          throw new Error("Banner upload failed");
        }
      }
    } catch (e) {
      console.error(e);
      setIsSubmitting(false);
      setError("Failed to upload images. Please try again.");
      return;
    }

    // Double check we aren't sending blobs
    if (
      finalLogoUrl?.startsWith("blob:") ||
      finalBannerUrl?.startsWith("blob:")
    ) {
      setIsSubmitting(false);
      setError("Image upload incomplete. Please try again.");
      return;
    }

    const endpoint = Endpoints.CreateCampaign();
    const payload = {
      title: form.values.title,
      clientName: form.values.clientName,
      description: form.values.description,
      status: form.values.status,
      endDate: form.values.endDate,
      minLevel: form.values.minLevel,
      branding: {
        primaryColor: form.values.primaryColor,
        secondaryColor: form.values.secondaryColor,
        logoUrl: finalLogoUrl,
        bannerUrl: finalBannerUrl,
      },
      eligibility: form.values.eligibility,
      completionBonus: form.values.completionBonus,
      isLocked: form.values.isLocked,
      lockReason: form.values.isLocked ? form.values.lockReason : undefined,
      squadsOnly: form.values.squadsOnly,
    };

    const r = await PerformRequest({
      method: endpoint.method,
      route: endpoint.route,
      data: payload,
      onError: () => setError("Failed to create campaign"),
    });

    if (r.status === HttpCodes.Created || r.status === HttpCodes.Success) {
      navigate("/d/campaigns");
    } else {
      setError(r.message || "Failed to create campaign");
    }
    setIsSubmitting(false);
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #0F0518 0%, #1A0B2E 100%)",
        paddingBottom: 40,
      }}
    >
      <div
        className="header-bg"
        style={{
          height: 200,
          background: "linear-gradient(135deg, #145223 0%, #19752e 100%)",
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          borderBottomRightRadius: 40,
          borderBottomLeftRadius: 40,
        }}
      ></div>

      <Container
        size="xl"
        style={{ position: "relative", zIndex: 1, paddingTop: 40 }}
      >
        <Stack gap="xl">
          {/* Header */}
          <Group justify="space-between" align="center">
            <div>
              <Button
                variant="white"
                color="dark"
                size="xs"
                radius="xl"
                leftSection={<IconArrowLeft size={16} />}
                onClick={() => navigate(-1)}
                mb={12}
                style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}
              >
                Back to Campaigns
              </Button>
              <Title order={1} c="white">
                Create Campaign
              </Title>
              <Text c="rgba(255,255,255,0.7)" size="sm">
                Design a new campaign and preview it instantly.
              </Text>
            </div>
            <Button
              size="md"
              radius="xl"
              variant="gradient"
              gradient={{ from: "brand", to: "green", deg: 45 }}
              leftSection={<IconDeviceFloppy size={20} />}
              loading={isSubmitting}
              onClick={submit}
              style={{ boxShadow: "0 4px 12px rgba(108, 77, 255, 0.4)" }}
            >
              Save Campaign
            </Button>
          </Group>

          {error && (
            <Alert color="red" variant="filled" radius="md">
              {error}
            </Alert>
          )}

          <SimpleGrid cols={{ base: 1, lg: 2 }} spacing={40}>
            {/* Left Column: Forms */}
            <Stack gap="xl">
              <Paper
                shadow="sm"
                p="xl"
                radius="lg"
                style={{
                  background: "rgba(255, 255, 255, 0.95)",
                  backdropFilter: "blur(20px)",
                  border: "1px solid rgba(255,255,255,0.1)",
                }}
              >
                <Group mb="lg">
                  <ThemeIcon
                    size="lg"
                    radius="md"
                    variant="light"
                    color="green"
                  >
                    <IconDetails size={20} />
                  </ThemeIcon>
                  <Title order={4}>Campaign Details</Title>
                </Group>

                <Stack gap="md">
                  <TextInput
                    size="md"
                    label="Campaign Title"
                    placeholder="e.g. Opay Winter Promo"
                    withAsterisk
                    {...form.getInputProps("title")}
                  />
                  <TextInput
                    size="md"
                    label="Client Name"
                    placeholder="e.g. Opay"
                    withAsterisk
                    {...form.getInputProps("clientName")}
                  />
                  <Textarea
                    size="md"
                    label="Description"
                    placeholder="Briefly describe the campaign goals..."
                    minRows={3}
                    {...form.getInputProps("description")}
                  />
                  <Select
                    size="md"
                    label="Status"
                    data={[
                      { value: "draft", label: "Draft" },
                      { value: "active", label: "Active" },
                      { value: "completed", label: "Completed" },
                    ]}
                    {...form.getInputProps("status")}
                  />
                  <DatePickerInput
                    size="md"
                    label="Expiry Date (Optional)"
                    placeholder="Pick a date"
                    minDate={new Date(Date.now() + 86400000)}
                    clearable
                    {...form.getInputProps("endDate")}
                  />
                </Stack>
              </Paper>

              <Paper
                shadow="sm"
                p="xl"
                radius="lg"
                style={{
                  background: "rgba(255, 255, 255, 0.95)",
                  backdropFilter: "blur(20px)",
                  border: "1px solid rgba(255,255,255,0.1)",
                }}
              >
                <Group mb="lg">
                  <ThemeIcon size="lg" radius="md" variant="light" color="pink">
                    <IconPalette size={20} />
                  </ThemeIcon>
                  <Title order={4}>Branding & Assets</Title>
                </Group>

                <Stack gap="md">
                  <SimpleGrid cols={2}>
                    <ColorInput
                      size="md"
                      label="Primary Color"
                      disallowInput={false}
                      {...form.getInputProps("primaryColor")}
                    />
                    <ColorInput
                      size="md"
                      label="Secondary Color"
                      disallowInput={false}
                      {...form.getInputProps("secondaryColor")}
                    />
                  </SimpleGrid>
                  <FileUploader
                    label="Logo"
                    value={form.values.logoUrl}
                    onChange={(url) => {
                      form.setFieldValue("logoUrl", url);
                      if (!url) setLogoFile(null);
                    }}
                    onFileSelect={(file) => {
                      setLogoFile(file);
                      form.setFieldValue("logoUrl", URL.createObjectURL(file));
                    }}
                    placeholder="Upload Logo"
                  />
                  <FileUploader
                    label="Banner"
                    value={form.values.bannerUrl}
                    onChange={(url) => {
                      form.setFieldValue("bannerUrl", url);
                      if (!url) setBannerFile(null);
                    }}
                    onFileSelect={(file) => {
                      setBannerFile(file);
                      form.setFieldValue(
                        "bannerUrl",
                        URL.createObjectURL(file),
                      );
                    }}
                    placeholder="Upload Banner"
                  />
                </Stack>
              </Paper>

              <Paper
                shadow="sm"
                p="xl"
                radius="lg"
                style={{
                  background: "rgba(255, 255, 255, 0.95)",
                  backdropFilter: "blur(20px)",
                  border: "1px solid rgba(255,255,255,0.1)",
                }}
              >
                <Group mb="lg">
                  <ThemeIcon
                    size="lg"
                    radius="md"
                    variant="light"
                    color="orange"
                  >
                    <IconLock size={20} />
                  </ThemeIcon>
                  <Title order={4}>Eligibility Criteria</Title>
                </Group>

                <Stack gap="md">
                  <Select
                    size="md"
                    label="Access Type"
                    data={[
                      { value: "open", label: "Open for All" },
                      { value: "criteria", label: "Criteria Based" },
                    ]}
                    {...form.getInputProps("eligibility.type")}
                  />

                  {form.values.eligibility.type === "criteria" && (
                    <NumberInput
                      size="md"
                      label="Minimum Activity Completions"
                      description="Users must have completed this many activities to join."
                      min={0}
                      {...form.getInputProps(
                        "eligibility.minMissionCompletions",
                      )}
                    />
                  )}

                  <Select
                    size="md"
                    label="Minimum User Level"
                    description="Users must reach this level to access this campaign."
                    data={levels
                      .sort((a, b) => a.level - b.level)
                      .map((l) => ({
                        value: String(l.level),
                        label: `Level ${l.level} - ${l.name} (${l.minCoins}+ coins)`,
                      }))}
                    value={String(form.values.minLevel)}
                    onChange={(v) =>
                      form.setFieldValue("minLevel", Number(v) || 1)
                    }
                  />

                  <Divider my="sm" />

                  <Switch
                    label="Squads Only Campaign"
                    description="If enabled, only users who are members of a Squad can participate in this campaign."
                    size="md"
                    {...form.getInputProps("squadsOnly", { type: "checkbox" })}
                  />

                  <Divider my="sm" />

                  <Switch
                    label="Lock Campaign"
                    description="Prevent users from participating while displaying a lock reason"
                    size="md"
                    {...form.getInputProps("isLocked", { type: "checkbox" })}
                  />

                  {form.values.isLocked && (
                    <TextInput
                      size="md"
                      label="Lock Reason"
                      description="Short message explaining why it's locked (e.g., 'Requires VIP status')"
                      withAsterisk
                      {...form.getInputProps("lockReason")}
                    />
                  )}
                </Stack>
              </Paper>

              <Paper
                shadow="sm"
                p="xl"
                radius="lg"
                style={{
                  background: "rgba(255, 255, 255, 0.95)",
                  backdropFilter: "blur(20px)",
                  border: "1px solid rgba(255,255,255,0.1)",
                }}
              >
                <Group mb="lg">
                  <ThemeIcon size="lg" radius="md" variant="light" color="teal">
                    <IconGift size={20} />
                  </ThemeIcon>
                  <Title order={4}>Rewards & Gamification</Title>
                </Group>

                <Stack gap="md">
                  <SimpleGrid cols={2}>
                    <NumberInput
                      size="md"
                      label="Completion Bonus"
                      description="Reward for completing all activities"
                      min={0}
                      rightSection={
                        <Text size="xs" mr="xs">
                          Coins
                        </Text>
                      }
                      {...form.getInputProps("completionBonus.amount")}
                    />
                    <TextInput
                      size="md"
                      label="Currency"
                      description="Currency code (e.g. COIN)"
                      {...form.getInputProps("completionBonus.currency")}
                    />
                  </SimpleGrid>
                </Stack>
              </Paper>
            </Stack>

            {/* Right Column: Preview */}
            <Stack
              gap="md"
              align="center"
              style={{ position: "sticky", top: 20 }}
            >
              <Group>
                <ThemeIcon radius="xl" color="gray" variant="light">
                  <IconPhoneCheck size={18} />
                </ThemeIcon>
                <Text fw={600} c="dimmed" size="sm" tt="uppercase">
                  Live Mobile Preview
                </Text>
              </Group>

              {/* Phone Frame */}
              <Box
                style={{
                  width: 320,
                  height: 640,
                  background: "#fff",
                  borderRadius: 40,
                  border: "8px solid #333",
                  boxShadow: "0 20px 40px rgba(0,0,0,0.2)",
                  overflow: "hidden",
                  position: "relative",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {/* Status Bar Mock */}
                <Group justify="space-between" px="lg" pt="md" pb="xs">
                  <Text size="xs" fw={700}>
                    9:41
                  </Text>
                  <Group gap={4}>
                    <div
                      style={{
                        width: 14,
                        height: 10,
                        background: "#000",
                        borderRadius: 2,
                      }}
                    />
                  </Group>
                </Group>

                {/* App Content */}
                <div
                  style={{ flex: 1, overflowY: "auto", position: "relative" }}
                >
                  {/* Detailed Campaign View Mock */}
                  <div
                    style={{
                      height: 180,
                      background: form.values.bannerUrl
                        ? `url(${form.values.bannerUrl}) center/cover`
                        : `${form.values.primaryColor}20`,
                      position: "relative",
                      display: "flex",
                      alignItems: "flex-end",
                      padding: 16,
                    }}
                  >
                    {!form.values.bannerUrl && (
                      <Text
                        size="xs"
                        c="dimmed"
                        ta="center"
                        style={{
                          width: "100%",
                          position: "absolute",
                          top: "40%",
                        }}
                      >
                        Banner Image
                      </Text>
                    )}
                  </div>

                  <div
                    style={{
                      padding: 20,
                      marginTop: -20,
                      position: "relative",
                    }}
                  >
                    <Card
                      radius="lg"
                      shadow="sm"
                      p="lg"
                      style={{ background: "white" }}
                    >
                      <Group align="flex-start" wrap="nowrap">
                        <Avatar
                          src={form.values.logoUrl}
                          size="lg"
                          radius="md"
                          style={{
                            background: form.values.primaryColor,
                            color: "white",
                          }}
                        >
                          {form.values.clientName?.[0] || "C"}
                        </Avatar>
                        <div>
                          <Text fw={700} size="lg" lh={1.2}>
                            {form.values.title || "Campaign Title"}
                          </Text>
                          <Text size="sm" c="dimmed">
                            {form.values.clientName || "Client Name"}
                          </Text>
                        </div>
                      </Group>

                      <Divider my="md" />

                      <Text size="xs" fw={700} c="dimmed" tt="uppercase" mb={4}>
                        About
                      </Text>
                      <Text size="sm" c="gray.7" lineClamp={3}>
                        {form.values.description ||
                          "This is where the campaign description will appear in the app. Users will read this to understand the campaign."}
                      </Text>

                      <Group mt="lg">
                        <Badge
                          color={
                            form.values.status === "active" ? "green" : "gray"
                          }
                          variant="light"
                        >
                          {form.values.status}
                        </Badge>
                        <Badge color="brand" variant="light">
                          5 Activities
                        </Badge>
                      </Group>
                    </Card>

                    <Stack mt="md">
                      <Card shadow="xs" radius="md" p="sm" withBorder>
                        <Group justify="space-between">
                          <Group gap="xs">
                            <Box
                              w={32}
                              h={32}
                              bg={form.values.secondaryColor}
                              style={{ borderRadius: 8 }}
                            />
                            <div>
                              <Text size="sm" fw={600}>
                                Example Activity
                              </Text>
                              <Text size="xs" c="dimmed">
                                Spotify • 50 Coins
                              </Text>
                            </div>
                          </Group>
                          <Button
                            size="xs"
                            radius="xl"
                            variant="light"
                            color="gray"
                          >
                            Start
                          </Button>
                        </Group>
                      </Card>
                    </Stack>
                  </div>
                </div>

                {/* Bottom Bar Mock */}
                <div
                  style={{
                    padding: 16,
                    borderTop: "1px solid #eee",
                    background: "white",
                  }}
                >
                  <Button
                    fullWidth
                    radius="xl"
                    style={{
                      background: form.values.primaryColor,
                      boxShadow: `0 4px 12px ${form.values.primaryColor}66`,
                    }}
                  >
                    Join Campaign
                  </Button>
                </div>
              </Box>
            </Stack>
          </SimpleGrid>
        </Stack>
      </Container>
    </div>
  );
};

export default CampaignCreate;
